import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { Pencil, Plus, Trash2 } from "lucide-react";

import { ConfirmDelete } from "@/components/admin/ConfirmDelete";
import { SelectField, TextField } from "@/components/admin/fields";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { toast } from "@/components/feedback/ToastHost";
import { errorMessage } from "@/lib/errors";
import { hasPermission, Permission } from "@/lib/permissions";
import { useAuth } from "@/stores/authStore";
import {
  createContact,
  deleteContact,
  listContacts,
  updateContact,
} from "@/services/api/contacts";
import type { Contact, ContactOwnerType } from "@/services/api/types";

interface EntityContactsProps {
  ownerType: ContactOwnerType;
  /** Owner id; pass null on a create form (no record yet → disabled section). */
  ownerId: number | null;
  /** Read-only list (view forms); omit for the editable list (edit forms). */
  readOnly?: boolean;
}

interface ContactForm {
  id: string;
  first_name: string;
  surname: string;
  phone: string;
  email: string;
  role: string;
  parent_id: string;
}

const emptyForm: ContactForm = {
  id: "",
  first_name: "",
  surname: "",
  phone: "",
  email: "",
  role: "",
  parent_id: "",
};

const contactName = (c: Pick<Contact, "first_name" | "surname">): string =>
  [c.first_name, c.surname].filter(Boolean).join(" ") || "—";

/** An entity's contacts list — read-only on view forms, editable on edit forms. */
export function EntityContacts({ ownerType, ownerId, readOnly = false }: EntityContactsProps) {
  const { t } = useTranslation();
  const { user } = useAuth();
  const permissions = user?.permissions ?? [];
  const canRead = hasPermission(permissions, Permission.CONTACTS_READ);
  const editable =
    ownerId != null && hasPermission(permissions, Permission.CONTACTS_WRITE) && !readOnly;
  const queryClient = useQueryClient();
  const [dialog, setDialog] = useState<
    { mode: "create" } | { mode: "edit"; contact: Contact } | null
  >(null);
  const [deleting, setDeleting] = useState<Contact | null>(null);
  const [form, setForm] = useState<ContactForm>(emptyForm);

  const query = useQuery({
    queryKey: ["contacts", ownerType, ownerId],
    queryFn: () =>
      listContacts({ owner_type: ownerType, owner_id: ownerId as number, limit: 100 }),
    enabled: canRead && ownerId != null,
  });
  const contacts = query.data?.items ?? [];

  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: ["contacts", ownerType, ownerId] });
    void queryClient.invalidateQueries({ queryKey: ["contacts"] });
  };

  const openCreate = () => {
    setForm(emptyForm);
    setDialog({ mode: "create" });
  };

  const openEdit = (contact: Contact) => {
    setForm({
      id: String(contact.id),
      first_name: contact.first_name ?? "",
      surname: contact.surname ?? "",
      phone: contact.phone ?? "",
      email: contact.email ?? "",
      role: contact.role ?? "",
      parent_id: contact.parent_id?.toString() ?? "",
    });
    setDialog({ mode: "edit", contact });
  };

  const save = useMutation({
    mutationFn: async () => {
      const payload = {
        first_name: form.first_name || null,
        surname: form.surname || null,
        phone: form.phone || null,
        email: form.email || null,
        role: form.role || null,
        parent_id: form.parent_id ? Number(form.parent_id) : null,
      };
      if (dialog?.mode === "edit" && dialog.contact) {
        return updateContact(dialog.contact.id, payload);
      }
      return createContact({ owner_type: ownerType, owner_id: ownerId as number, ...payload });
    },
    onSuccess: () => {
      toast.success(t("admin.saved"));
      setDialog(null);
      refresh();
    },
    onError: (error) => toast.error(errorMessage(error)),
  });

  const remove = useMutation({
    mutationFn: (id: number) => deleteContact(id),
    onSuccess: () => {
      toast.success(t("admin.deleted"));
      setDeleting(null);
      refresh();
    },
    onError: (error) => toast.error(errorMessage(error)),
  });

  const parentOptions = contacts
    .filter((c) => c.id !== Number(form.id))
    .map((c) => ({ value: c.id.toString(), label: contactName(c) }));

  if (!canRead) return null;

  return (
    <div className="rounded-md border p-3">
      <div className="mb-2 flex items-center justify-between">
        <Label className="text-sm font-semibold">{t("admin.contacts")}</Label>
        {ownerId == null ? (
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled
            title={t("admin.saveBeforeContacts")}
          >
            <Plus className="h-4 w-4" /> {t("admin.addContact")}
          </Button>
        ) : editable ? (
          <Button type="button" variant="outline" size="sm" onClick={openCreate}>
            <Plus className="h-4 w-4" /> {t("admin.addContact")}
          </Button>
        ) : null}
      </div>

      {contacts.length === 0 ? (
        <p className="text-sm text-muted-foreground">—</p>
      ) : (
        <ul className="space-y-2">
          {contacts.map((contact) => (
            <li
              key={contact.id}
              className="flex items-center justify-between gap-2 rounded border bg-muted/20 px-3 py-2 text-sm"
            >
              <div className="min-w-0">
                <div className="truncate font-medium">{contactName(contact)}</div>
                <div className="truncate text-xs text-muted-foreground">
                  {[contact.role, contact.phone, contact.email].filter(Boolean).join(" · ") || "—"}
                </div>
              </div>
              {editable ? (
                <div className="flex shrink-0 items-center gap-1">
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="h-7 w-7"
                    onClick={() => openEdit(contact)}
                    aria-label={t("common.edit")}
                    title={t("common.edit")}
                  >
                    <Pencil className="h-4 w-4" />
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="h-7 w-7 text-destructive hover:text-destructive"
                    onClick={() => setDeleting(contact)}
                    aria-label={t("common.delete")}
                    title={t("common.delete")}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              ) : null}
            </li>
          ))}
        </ul>
      )}

      <Dialog open={dialog !== null} onOpenChange={(open) => !open && setDialog(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>
              {dialog?.mode === "edit" ? t("admin.edit") : t("admin.create")}
            </DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-4">
            <TextField
              label={t("admin.firstName")}
              value={form.first_name}
              onChange={(v) => setForm({ ...form, first_name: v })}
            />
            <TextField
              label={t("admin.surname")}
              value={form.surname}
              onChange={(v) => setForm({ ...form, surname: v })}
            />
            <TextField
              label={t("admin.phone")}
              value={form.phone}
              onChange={(v) => setForm({ ...form, phone: v })}
            />
            <TextField
              label={t("admin.email")}
              value={form.email}
              onChange={(v) => setForm({ ...form, email: v })}
            />
            <SelectField
              label={t("admin.parentContact")}
              value={form.parent_id}
              onChange={(v) => setForm({ ...form, parent_id: v })}
              options={[{ value: "", label: "—" }, ...parentOptions]}
            />
            <TextField
              label={t("admin.role")}
              value={form.role}
              onChange={(v) => setForm({ ...form, role: v })}
            />
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={() => setDialog(null)}>
              {t("common.cancel")}
            </Button>
            <Button
              onClick={() => save.mutate()}
              disabled={save.isPending || !(form.first_name || form.surname)}
            >
              {t("common.save")}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <ConfirmDelete
        open={deleting !== null}
        onOpenChange={(open) => !open && setDeleting(null)}
        title={t("admin.deleteRecord", { name: deleting ? contactName(deleting) : "" })}
        description={t("admin.deleteRecordDescription")}
        onConfirm={async () => {
          if (deleting) await remove.mutateAsync(deleting.id);
        }}
      />
    </div>
  );
}
